/**
 * DynamicReadingPanel — Lecture dynamique des asymétries (V23.14.7c).
 *
 * Ouverture depuis Asymetries.jsx, pattern async job (Cloudflare-safe).
 *
 * Affichage :
 *   - Diagnostic Gauss expertise vs visibilité (percentiles)
 *   - 3-5 concurrents qui communiquent mieux
 *   - 3 questions Noam pour aider à se positionner
 *   - 3-5 leviers d'action 30j
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  X, Loader2, Sparkles, RefreshCw, AlertTriangle, ExternalLink,
  TrendingUp, MessageCircle, Lightbulb, Send,
} from "lucide-react";
import FreshnessBadge from "./FreshnessBadge";

function OrgDNAWidget() {
  const [data, setData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    try {
      const r = await api.get("/workspace/organisation/dna/latest");
      if (r.data?.available) setData(r.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchLatest(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [fetchLatest]);

  const extract = async () => {
    setExtracting(true); setError(null);
    try {
      const r = await api.post("/workspace/organisation/dna/extract", {});
      const job_id = r.data?.job_id;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/workspace/organisation/dna/jobs/${job_id}`);
          const d = s.data || {};
          if (d.status === "done") { clearInterval(pollRef.current); await fetchLatest(); setExtracting(false); notify.success("ADN organisationnel mis à jour."); }
          else if (d.status === "failed") { clearInterval(pollRef.current); setError(d.error || "Erreur"); setExtracting(false); }
        } catch (e) { clearInterval(pollRef.current); setError(fmtError(e)); setExtracting(false); }
      }, 2000);
    } catch (e) { setError(fmtError(e)); setExtracting(false); }
  };

  return (
    <div className="border border-[#EFD127]/30 bg-[#EFD127]/[0.04] rounded-md p-3" data-testid="org-dna-widget">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="w-3.5 h-3.5 text-[#EFD127]" />
          <span className="text-sm font-medium text-zinc-100">ADN organisationnel (mémoire Q&amp;A)</span>
          {data && <FreshnessBadge lastGeneratedAt={data.created_at} moduleLabel="ADN org" testid="org-dna-freshness" />}
        </div>
        <button onClick={extract} disabled={extracting} className="text-[11px] px-2 py-0.5 rounded border border-white/10 hover:bg-white/5 disabled:opacity-50" data-testid="org-dna-extract-btn">
          {extracting ? <><Loader2 className="w-3 h-3 inline animate-spin" /> Extraction…</> : (data ? <><RefreshCw className="w-3 h-3 inline" /> Rafraîchir</> : <><Sparkles className="w-3 h-3 inline" /> Extraire l&apos;ADN</>)}
        </button>
      </div>
      {error && <div className="text-[11px] text-[#E24B4A] mb-2" data-testid="org-dna-error">{error}</div>}
      {!data && !extracting && (
        <p className="text-[11px] text-zinc-400 italic">Répondez aux 3 questions Noam dans plusieurs Lectures Dynamiques pour que Noam apprenne votre ADN stratégique.</p>
      )}
      {data && (
        <div className="space-y-2" data-testid="org-dna-results">
          <div className="text-[10px] text-zinc-500">Basé sur <b>{data.based_on_sessions}</b> session(s) de Q&amp;A</div>
          {(data.dna_traits || []).slice(0, 4).map((t, i) => (
            <div key={i} className="text-[11px]" data-testid={`org-dna-trait-${i}`}>
              <span className="text-zinc-100 font-medium">{t.trait}</span>
              <span className="text-[10px] px-1 py-0 ml-1.5 rounded bg-white/5 text-zinc-500">conf. {t.confidence_0_100 ?? "?"}</span>
              <div className="text-zinc-400 italic mt-0.5">{t.strategic_implication}</div>
            </div>
          ))}
          {data.recommended_next_focus && (
            <div className="text-[11px] text-[#7F77DD] border-l-2 border-[#7F77DD] pl-2 mt-2">
              <span className="font-semibold">Prochain focus :</span> {data.recommended_next_focus}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DynamicReadingPanel({ open, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | running | done | failed
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [reading, setReading] = useState(null);
  const [answers, setAnswers] = useState({}); // {q_idx: text}
  const [submittingAnswers, setSubmittingAnswers] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // Fetch latest on open
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/asymetries/dynamic-reading/latest");
        if (cancelled) return;
        if (r.data?.available) {
          setReading(r.data);
          setPhase("done");
          // Restore answers if present
          setAnswers(r.data.user_answers || {});
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; stopPolling(); };
  }, [open, stopPolling]);

  const startReading = useCallback(async (extraAnswers = null) => {
    setPhase("running");
    setProgress("Démarrage…");
    setError(null);
    try {
      const r = await api.post("/workspace/asymetries/dynamic-reading/start", {
        user_answers: extraAnswers || null,
        parent_reading_id: reading?.id || null,
      });
      const job_id = r.data?.job_id;
      if (!job_id) throw new Error("Pas de job_id retourné.");
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/workspace/asymetries/dynamic-reading/jobs/${job_id}`);
          const data = s.data || {};
          setProgress(data.progress || "");
          if (data.status === "done") {
            stopPolling();
            const latest = await api.get("/workspace/asymetries/dynamic-reading/latest");
            if (latest.data?.available) {
              setReading(latest.data);
              setAnswers(latest.data.user_answers || {});
            }
            setPhase("done");
            notify.success("Lecture dynamique générée.");
          } else if (data.status === "failed") {
            stopPolling();
            setError(data.error || "Erreur inconnue.");
            setPhase("failed");
          }
        } catch (e) {
          stopPolling();
          setError(fmtError(e));
          setPhase("failed");
        }
      }, 2000);
    } catch (e) {
      setError(fmtError(e));
      setPhase("failed");
    }
  }, [reading?.id, stopPolling]);

  const submitAnswers = useCallback(async () => {
    setSubmittingAnswers(true);
    try {
      await startReading(answers);
    } finally {
      setSubmittingAnswers(false);
    }
  }, [answers, startReading]);

  if (!open) return null;

  const gauss = reading?.gauss_diagnostic || {};
  const competitors = reading?.competitors || [];
  const questions = reading?.noam_questions || [];
  const levers = reading?.actionable_levers || [];

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="asym-dyn-modal"
    >
      <div
        className="noam-card max-w-4xl w-full my-8 p-6 space-y-5 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={phase === "running"}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white disabled:opacity-30"
          data-testid="asym-dyn-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#7F77DD]/15 border border-[#7F77DD]/30 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-[#7F77DD]" />
          </div>
          <div>
            <div className="noam-overline">Doctrine 47 · Asymétries dynamiques</div>
            <div className="noam-display text-xl font-semibold">Lecture vivante du positionnement</div>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl leading-relaxed">
              Noam croise votre profil + concurrents identifiés sur le web (Tavily) + courbe de Gauss pour diagnostiquer où vous êtes vraiment positionné, et poser 3 questions concrètes pour vous aider à progresser. Honnêteté stricte : si Noam manque de signal, il le dit.
            </p>
          </div>
        </div>

        {/* Action principale */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => startReading(null)}
            disabled={phase === "running"}
            className="btn-primary text-sm disabled:opacity-50"
            data-testid="asym-dyn-start-btn"
          >
            {phase === "running" ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {progress || "Analyse en cours…"}</>
            ) : phase === "done" ? (
              <><RefreshCw className="w-4 h-4" /> Relancer une lecture dynamique</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Lancer la lecture dynamique</>
            )}
          </button>
          {phase === "done" && reading && (
            <FreshnessBadge
              lastGeneratedAt={reading.created_at}
              moduleLabel="Lecture dynamique"
              testid="asym-dyn-freshness"
            />
          )}
        </div>

        {/* Erreur */}
        {phase === "failed" && error && (
          <div className="p-3 rounded border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-sm text-[#E24B4A]" data-testid="asym-dyn-error">
            <AlertTriangle className="w-4 h-4 inline mr-1" /> {error}
          </div>
        )}

        {/* Résultats */}
        {phase === "done" && reading && (
          <div className="space-y-5" data-testid="asym-dyn-results">

            {/* Diagnostic Gauss */}
            <div className="border border-white/10 rounded-md p-4">
              <div className="noam-overline mb-2">Diagnostic Gauss · Expertise vs Visibilité</div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Expertise (percentile)</div>
                  <div className="text-2xl font-bold text-[#1D9E75]" data-testid="asym-dyn-exp-pct">
                    {gauss.expertise_percentile_0_100 != null ? `${gauss.expertise_percentile_0_100}%` : "N/A"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Visibilité (percentile)</div>
                  <div className="text-2xl font-bold text-[#EF9F27]" data-testid="asym-dyn-vis-pct">
                    {gauss.visibility_percentile_0_100 != null ? `${gauss.visibility_percentile_0_100}%` : "N/A"}
                  </div>
                </div>
              </div>
              {gauss.interpretation && (
                <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap border-l-2 border-[#7F77DD] pl-3" data-testid="asym-dyn-interpretation">
                  {gauss.interpretation}
                </p>
              )}
            </div>

            {/* Concurrents — Accordéon collapsible */}
            {competitors.length > 0 && (
              <details className="border border-white/10 rounded-md p-4 group" data-testid="asym-dyn-competitors-section">
                <summary className="noam-overline cursor-pointer hover:text-zinc-100 list-none flex items-center gap-2 select-none">
                  <span className="inline-block transition-transform group-open:rotate-90 text-zinc-500">▸</span>
                  <span>Concurrents qui communiquent mieux ({competitors.length})</span>
                </summary>
                <div className="space-y-2 mt-3">
                  {competitors.map((c, i) => (
                    <div key={i} className="rounded p-3 bg-white/[0.02] border border-white/5" data-testid={`asym-dyn-competitor-${i}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="text-sm font-medium text-zinc-100 flex items-center gap-2 flex-wrap">
                          {c.name}
                          {c.url && (
                            <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#378ADD] hover:underline inline-flex items-center gap-0.5">
                              <ExternalLink className="w-3 h-3" /> Voir
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[10px]">
                          {c.perceived_strength_0_100 != null && (
                            <span className="px-1.5 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27]">
                              Perçu {c.perceived_strength_0_100}
                            </span>
                          )}
                          {c.actual_competence_estimate_0_100 != null && (
                            <span className="px-1.5 py-0.5 rounded bg-[#1D9E75]/15 text-[#1D9E75]">
                              Réel {c.actual_competence_estimate_0_100}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">
                        {c.why_better_communication}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Questions Noam */}
            {questions.length > 0 && (
              <div className="border border-[#7F77DD]/30 bg-[#7F77DD]/[0.05] rounded-md p-4" data-testid="asym-dyn-questions">
                <div className="noam-overline mb-2 flex items-center gap-1.5">
                  <MessageCircle className="w-3 h-3 text-[#7F77DD]" /> 3 questions pour vous aider à progresser
                </div>
                <div className="space-y-3">
                  {questions.map((q, i) => (
                    <div key={i} className="space-y-1.5" data-testid={`asym-dyn-question-${i}`}>
                      <div className="text-sm text-zinc-100 font-medium">{q.question}</div>
                      {q.why_this_matters && (
                        <div className="text-[11px] text-zinc-500 italic">Pourquoi : {q.why_this_matters}</div>
                      )}
                      {q.example_good_answer && (
                        <div className="text-[11px] text-zinc-500">Exemple : « {q.example_good_answer} »</div>
                      )}
                      <textarea
                        rows={2}
                        value={answers[String(i)] || ""}
                        onChange={(e) => setAnswers((s) => ({ ...s, [String(i)]: e.target.value }))}
                        placeholder="Votre réponse…"
                        className="w-full mt-1 px-2 py-1.5 text-xs bg-white/[0.04] border border-white/10 rounded text-zinc-200 placeholder:text-zinc-600 focus:border-[#7F77DD]/50 focus:outline-none"
                        data-testid={`asym-dyn-answer-${i}`}
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={submitAnswers}
                  disabled={submittingAnswers || phase === "running" || Object.values(answers).every((v) => !v?.trim())}
                  className="btn-primary text-xs mt-3 disabled:opacity-50"
                  data-testid="asym-dyn-submit-answers"
                >
                  {submittingAnswers ? <><Loader2 className="w-3 h-3 animate-spin" /> Envoi…</> : <><Send className="w-3 h-3" /> Envoyer mes réponses & relancer l&apos;analyse</>}
                </button>
              </div>
            )}

            {/* V23.14.8 — ADN organisationnel (apprentissage Q&A) */}
            <OrgDNAWidget />

            {/* Leviers actionnables */}
            {levers.length > 0 && (
              <details open className="border border-[#EFD127]/30 bg-[#EFD127]/[0.04] rounded-md p-4 group" data-testid="asym-dyn-levers-section">
                <summary className="noam-overline cursor-pointer hover:text-zinc-100 list-none flex items-center gap-2 select-none">
                  <span className="inline-block transition-transform group-open:rotate-90 text-zinc-500">▸</span>
                  <Lightbulb className="w-3 h-3 text-[#EFD127]" /> Leviers d&apos;action 30 jours ({levers.length})
                </summary>
                <ul className="space-y-1.5 text-sm mt-3" data-testid="asym-dyn-levers">
                  {levers.map((l, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#EFD127] font-bold text-xs mt-0.5">{i + 1}.</span>
                      <div className="flex-1">
                        <div className="text-zinc-100 font-medium text-sm">{l.title || l.label || `Levier ${i + 1}`}</div>
                        {(l.description || l.detail) && (
                          <div className="text-xs text-zinc-400 mt-0.5">{l.description || l.detail}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {/* Sources web — accordéon repliable */}
            {(reading.web_sources || []).length > 0 && (
              <details className="pt-3 border-t border-white/5 group" data-testid="asym-dyn-sources-section">
                <summary className="noam-overline cursor-pointer hover:text-zinc-100 list-none flex items-center gap-2 select-none">
                  <span className="inline-block transition-transform group-open:rotate-90 text-zinc-500">▸</span>
                  Sources web ({reading.web_sources.length})
                </summary>
                <ul className="space-y-0.5 mt-2">
                  {reading.web_sources.slice(0, 6).map((s, i) => (
                    <li key={i} className="text-[10px] text-zinc-500 truncate">
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#378ADD]">{s.title}</a>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
